/**
 * Tenant route capabilities from GET /api/tenant/features/capabilities.
 */

import React, { useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { tenantFeaturesService } from '../services/TenantFeaturesService';
import type { TenantCapabilitiesResponse } from '@cnapse/shared/schemas.js';
import { useAuth } from '../context/AppContext';
import { useSubdomain } from './SubdomainContext';
import {
  TenantFeaturesContext,
  type TenantFeaturesContextType,
} from './tenantFeaturesContextRef';

 

export type { TenantFeaturesContextType };

interface TenantFeaturesProviderProps {
  children: ReactNode;
}

export function TenantFeaturesProvider({ children }: TenantFeaturesProviderProps): React.JSX.Element {
  const { user } = useAuth();
  const { isActive, isLoading: subscriptionLoading } = useSubdomain();
  const [routes, setRoutes] = useState<Record<string, boolean>>({});
  const [modules, setModules] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCapabilities = useCallback(async () => {
    if (!user || user.role.startsWith('provider_')) {
      setIsLoading(false);
      setRoutes({});
      setModules({});
      return;
    }

    if (subscriptionLoading) {
      return;
    }

    if (!isActive) {
      setIsLoading(false);
      setRoutes({});
      setModules({});
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const capabilitiesResponse: TenantCapabilitiesResponse = await tenantFeaturesService.getTenantCapabilities();

      if (capabilitiesResponse.success && capabilitiesResponse.data) {
        setRoutes(capabilitiesResponse.data.routes);
        setModules(capabilitiesResponse.data.modules);
      } else {
        throw new Error('Failed to fetch tenant capabilities');
      }
    } catch (err) {
      console.error('Error fetching tenant capabilities:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setRoutes({});
      setModules({});
    } finally {
      setIsLoading(false);
    }
  }, [user, isActive, subscriptionLoading]);

  useEffect(() => {
    void fetchCapabilities();
  }, [fetchCapabilities]);

  const isRouteEnabled = useCallback((routeKey: string): boolean => {
    return routes[routeKey] ?? false;
  }, [routes]);

  const hasSpatialModule = modules.spatial_connect === true || modules.spatial_uwb === true;

  const value: TenantFeaturesContextType = {
    routes,
    modules,
    hasSpatialModule,
    isRouteEnabled,
    isLoading: subscriptionLoading || isLoading,
    error,
    refetch: fetchCapabilities,
  };

  return (
    <TenantFeaturesContext.Provider value={value}>
      {children}
    </TenantFeaturesContext.Provider>
  );
}

export function useTenantFeatures(): TenantFeaturesContextType {
  const context = useContext(TenantFeaturesContext);

  if (context === undefined) {
    throw new Error('useTenantFeatures must be used within a TenantFeaturesProvider');
  }

  return context;
}
